// Labels and chip colours for the ZKTeco / HPU attendance screens.
// Colours are plain MUI-friendly hex values so they work in the dark theme.

export const RESOLUTION_META = {
  auto: { label: "Matched (auto)", color: "#4caf50" },
  mapped: { label: "Mapped", color: "#26a69a" },
  needs_review: { label: "Needs review", color: "#ff9800" },
  unmapped: { label: "Not linked", color: "#f44336" },
  staff: { label: "Staff", color: "#7e57c2" },
  ignored: { label: "Ignored", color: "#9e9e9e" },
};

export const VERIFY_META = {
  face: { label: "Face", color: "#42a5f5" },
  fingerprint: { label: "Fingerprint", color: "#ab47bc" },
  card: { label: "Card", color: "#8d6e63" },
  password: { label: "Password", color: "#78909c" },
  other: { label: "Other", color: "#9e9e9e" },
};

export const MEMBER_STATUS_META = {
  active: { label: "Active", color: "#4caf50" },
  expired: { label: "Expired", color: "#f44336" },
  frozen: { label: "Frozen", color: "#4fc3f7" },
  grace_period: { label: "Grace period", color: "#f5a623" },
  suspended: { label: "Suspended", color: "#ff6b6b" },
  cancelled: { label: "Cancelled", color: "#9e9e9e" },
  inactive: { label: "Inactive", color: "#9e9e9e" },
  blacklisted: { label: "Blacklisted", color: "#ff1744" },
  blocked: { label: "Not eligible", color: "#ff6b6b" },
  pending: { label: "Pending", color: "#ff9800" },
  under_review: { label: "Under review", color: "#29b6f6" },
};

// Door access (Phase A dry-run): what TEBA would do per device PIN.
export const DECISION_META = {
  block: { label: "Would block", color: "#f44336" },
  allow: { label: "Allow", color: "#4caf50" },
  skip: { label: "Not managed", color: "#9e9e9e" },
};

export const APPLIED_META = {
  unknown: { label: "Device default", color: "#9e9e9e" },
  allowed: { label: "Allowed on device", color: "#4caf50" },
  blocked: { label: "Blocked on device", color: "#f44336" },
};

export const ACCESS_REASON_LABELS = {
  active: "Active membership",
  expired: "Membership expired",
  expired_grace_period: "Expired (grace period status)",
  within_grace_days: "Expired, within grace days",
  pending: "Registration pending",
  under_review: "Registration under review",
  pending_allowed: "Pending (allowed by setting)",
  under_review_allowed: "Under review (allowed by setting)",
  frozen: "Frozen (on hold)",
  frozen_allowed: "Frozen (allowed by setting)",
  admin_suspended: "Suspended by admin",
  admin_cancelled: "Cancelled by admin",
  admin_inactive: "Inactive (admin)",
  admin_blacklisted: "Blacklisted",
  override_allow: "Admin override: allow",
  override_block: "Admin override: block",
  expiry_unknown: "Active, expiry date unknown - check",
  unknown_status: "Unknown status - check",
  staff: "Staff (manual only)",
  ignored: "Ignored PIN",
  unmapped: "Not linked to a member",
  needs_review: "Needs review (name mismatch)",
  device_admin: "Device admin (manual only)",
  super_admin: "Super admin - protected",
  protected: "Protected",
  non_numeric_pin: "Non-numeric PIN",
  unknown_resolution: "Unknown PIN type",
  outside_pilot: "Outside pilot",
  restore_outside_pilot: "Restore: outside pilot",
  restore_staff: "Restore: now staff",
  restore_ignored: "Restore: now ignored",
  restore_unmapped: "Restore: no longer linked",
  restore_needs_review: "Restore: needs review",
  restore_device_admin: "Restore: device admin",
  restore_super_admin: "Restore: super admin",
  restore_protected: "Restore: protected PIN",
  unblock_all: "Unblock all",
};

export const ACCESS_EVENT_LABELS = {
  would_block: "Would block",
  would_unblock: "Would unblock",
  override_set: "Override set",
  override_revoked: "Override removed",
  queued_block: "Block sent",
  queued_unblock: "Unblock sent",
  applied: "Device confirmed",
  failed: "Device error",
  timeout: "No answer (timeout)",
  cancelled: "Command cancelled",
  refused: "Refused (protected)",
  drift: "Device differs",
  guard_tripped: "Guard stopped blocks",
  guard_approved: "Guard approved",
  unblock_all: "Unblock all",
  paused: "Paused",
  resumed: "Resumed",
};

// Door state column: command in flight / failed retry win over the confirmed state.
export const accessStateMeta = (row) => {
  if (["pending", "sent"].includes(row.cmd_status) && row.cmd_action) {
    return { label: `${row.cmd_action === "block" ? "Blocking" : "Unblocking"} (${row.cmd_status})`, color: "#29b6f6" };
  }
  if (row.attempts > 0) return { label: `Failed x${row.attempts}`, color: "#ff9800" };
  return metaFor(APPLIED_META, row.applied);
};

export const reasonLabel = (reason) => ACCESS_REASON_LABELS[reason] || reason || "-";

export const chipSx = (color) => ({
  backgroundColor: `${color}22`,
  color,
  border: `1px solid ${color}66`,
  fontWeight: 600,
});

export const metaFor = (map, key) => map[key] || { label: key || "-", color: "#9e9e9e" };

// "2026-10-07 05:50:39" -> "05:50"
export const timeOnly = (value) => (value ? String(value).slice(11, 16) : "-");

// Error text from an axios error (zkteco endpoints return {error}, but be defensive)
export const apiError = (error, fallback) =>
  error?.response?.data?.error || error?.message || fallback;
